// src/App.js - VERSION SEO OPTIMISÉE
import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';

import Header from './components/Header';
import Hero from './components/Hero';
import SearchForm from './components/SearchForm';
import About from './components/About';
import MusicianCard from './components/MusicianCard';
import StudioCard from './components/StudioCard';
import StudioSearchForm from './components/StudioSearchForm';
import TeacherSearchForm from './components/TeacherSearchForm';
import TeacherCard from './components/TeacherCard';
import { fetchTeachers } from './api/teachers';
import { fetchMusicians } from './api/musicians';
import { fetchStudios } from './api/studios';
import ContactButton from './components/ContactButton';
import Footer from './components/Footer';
import { Toaster } from 'react-hot-toast';
import PrivateRoute from './PrivateRoute';
import { Helmet } from 'react-helmet-async';
import EventCompanyCard from './components/EventCompanyCard';
import { fetchEventCompanies } from './api/eventCompanies';

// Import normal des pages (pas de lazy loading)
import MusicianProfile from './pages/MusicianProfile';
import TeacherProfile from './pages/TeacherProfile';
import StudioProfile from './pages/StudioProfile';
import AuthPage from './pages/AuthPage';
import ContactPage from './pages/ContactPage';
import MyProfile from './pages/MyProfile';
import { useImagePreloader } from './hooks/useImagePreloader';
import { imageCache } from './utils/imageCache';
import CompanyProfile from './pages/CompanyProfile';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AccountDeletion from './pages/AccountDeletion';

import { auth, db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import AddBookingDates from './pages/AddBookingDates';

// Cache pour éviter les appels API répétés
const apiCache = new Map();

const fetchWithCache = async (fetchFunction, params = {}, cacheTime = 5 * 60 * 1000) => {
  const cacheKey = JSON.stringify({ fn: fetchFunction.name, params });
  const cached = apiCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < cacheTime) {
    return cached.data;
  }

  const data = await fetchFunction(params);
  apiCache.set(cacheKey, { data, timestamp: Date.now() });
  return data;
};

function AdminRoute({ isAdmin, children }) {
  if (isAdmin === null) {
    return <p style={{ textAlign: 'center', marginTop: '40px', color: '#ccc' }}>Chargement...</p>;
  }
  if (!isAdmin) {
    return (
      <p style={{ textAlign: 'center', marginTop: '40px', color: '#f44336' }}>
        Accès refusé - Vous devez être admin pour accéder à cette page.
      </p>
    );
  }
  return children;
}

// Fonction getPageMetadata CORRIGÉE pour être dynamique
const getPageMetadata = (searchType = 'musicians', hasSearched = false, results = [], filters = {}) => {
  // Meta tags par défaut
  let title = "Gates of Music - Musiciens, Studios & Professeurs de Musique au Maroc";
  let description = "Trouvez l'artiste idéal qui saura enchanter vos soirées les plus folles. Nos musiciens sont les meilleurs du pays, bookez-les en quelques clics !";
  
  // Adaptation selon le type de recherche et les résultats
  if (hasSearched) {
    switch(searchType) {
      case 'musicians':
        if (filters?.city || filters?.genre) {
          title = `Musiciens ${filters.genre ? filters.genre + ' ' : ''}${filters.city ? 'à ' + filters.city : ''} - Gates of Music`;
          description = `Découvrez les meilleurs musiciens ${filters.genre ? 'de ' + filters.genre + ' ' : ''}${filters.city ? 'à ' + filters.city : 'au Maroc'}. ${results?.length || 0} artistes disponibles pour vos événements.`;
        }
        break;
      case 'studios':
        if (filters?.city) {
          title = `Studios d'enregistrement à ${filters.city} - Gates of Music`;
          description = `Trouvez le studio d'enregistrement parfait à ${filters.city}. ${results?.length || 0} studios professionnels disponibles.`;
        }
        break;
      case 'teachers':
        if (filters?.instrument || filters?.city) {
          title = `Professeurs ${filters.instrument ? 'de ' + filters.instrument + ' ' : ''}${filters.city ? 'à ' + filters.city : ''} - Gates of Music`;
          description = `Apprenez avec les meilleurs professeurs ${filters.instrument ? 'de ' + filters.instrument + ' ' : ''}${filters.city ? 'à ' + filters.city : 'au Maroc'}.`;
        }
        break;
      default:
        break;
    }
  }
  
  return { title, description };
};

// Composant HomePage avec SEO intégré
const HomePage = memo(({ musicians, setMusicians, filters, setFilters, hasSearched, setHasSearched, isAdmin }) => {
  const [searchType, setSearchType] = useState('musicians');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  const [studioCurrentPage, setStudioCurrentPage] = useState(1);
  const studiosPerPage = 4;
  const [teacherCurrentPage, setTeacherCurrentPage] = useState(1);
  const teachersPerPage = 4;

  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (!hasSearched) {
      fetchWithCache(fetchMusicians, {}).then(setMusicians);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [teachers, setTeachers] = useState([]);
  const [teacherFilters, setTeacherFilters] = useState({});
  const [hasSearchedTeachers, setHasSearchedTeachers] = useState(false);

  const [studios, setStudios] = useState([]);
  const [studioCityFilter, setStudioCityFilter] = useState('');
  const [hasSearchedStudios, setHasSearchedStudios] = useState(false);
  
  const [eventCompanies, setEventCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);

  const musicianImages = useMemo(() => 
    musicians.map(m => m.imageURL).filter(Boolean),
    [musicians]
  );
  const studioImages = useMemo(() => 
    studios.map(s => s.images?.[0]).filter(Boolean),
    [studios]
  );
  const teacherImages = useMemo(() => 
    teachers.map(t => t.image).filter(Boolean),
    [teachers]
  );

  const companyImages = useMemo(() => 
    eventCompanies.flatMap(c => [c.logo, c.coverImage]).filter(Boolean),
    [eventCompanies]
  );

  useImagePreloader(musicianImages);
  useImagePreloader(studioImages);
  useImagePreloader(teacherImages);
  useImagePreloader(companyImages);

  useEffect(() => {
    const loadStudios = async () => {
      const data = await fetchWithCache(fetchStudios);
      setStudios(data);
    };
    loadStudios();
  }, []);

  useEffect(() => {
    const loadEventCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        const data = await fetchWithCache(fetchEventCompanies, { limit: 6 });
        setEventCompanies(data);
      } catch (error) {
        console.error('Erreur lors du chargement des sociétés:', error);
        setEventCompanies([]);
      } finally {
        setIsLoadingCompanies(false);
      }
    };
    
    loadEventCompanies();
  }, []);

  // Callbacks mémorisés
  const handleSearch = useCallback(async (newFilters) => {
  const results = await fetchWithCache(fetchMusicians, newFilters);
  setMusicians(results);
  setFilters(newFilters);
  setHasSearched(true);
  setCurrentPage(1);
  
  // Précharger toutes les images des musiciens
  const imageUrls = results.map(m => m.imageURL).filter(Boolean);
    imageCache.preloadImages(imageUrls);
  }, [setMusicians, setFilters, setHasSearched]);

  const handleStudioSearch = useCallback((newFilters) => {
    setStudioCityFilter(newFilters.city);
    setHasSearchedStudios(true);
    
    // Précharger les images des studios filtrés
    const filtered = studios.filter(studio =>
      studio.city?.toLowerCase().includes(newFilters.city?.toLowerCase())
    );
    const imageUrls = filtered.map(s => s.images?.[0]).filter(Boolean);
    imageCache.preloadImages(imageUrls);
  }, [studios]);

  const handleTeacherSearch = useCallback(async (newFilters) => {
    const results = await fetchWithCache(fetchTeachers, newFilters);
    setTeachers(results);
    setTeacherFilters(newFilters);
    setHasSearchedTeachers(true);
    
    // Précharger toutes les images des professeurs
    const imageUrls = results.map(t => t.image).filter(Boolean);
    imageCache.preloadImages(imageUrls);
  }, []);

  // Mémoisation des données paginées
  const currentMusicians = useMemo(() => {
    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    return musicians.slice(indexOfFirst, indexOfLast);
  }, [musicians, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => Math.ceil(musicians.length / itemsPerPage), [musicians.length, itemsPerPage]);

  const filteredStudios = useMemo(() => 
    studioCityFilter
      ? studios.filter((studio) =>
          studio.city?.toLowerCase().includes(studioCityFilter.toLowerCase())
        )
      : studios,
    [studios, studioCityFilter]
  );

  const currentStudios = useMemo(() => {
    const indexOfLastStudio = studioCurrentPage * studiosPerPage;
    const indexOfFirstStudio = indexOfLastStudio - studiosPerPage;
    return filteredStudios.slice(indexOfFirstStudio, indexOfLastStudio);
  }, [filteredStudios, studioCurrentPage, studiosPerPage]);

  const totalStudioPages = useMemo(() => Math.ceil(filteredStudios.length / studiosPerPage), [filteredStudios.length, studiosPerPage]);

  const currentTeachers = useMemo(() => {
    const indexOfLastTeacher = teacherCurrentPage * teachersPerPage;
    const indexOfFirstTeacher = indexOfLastTeacher - teachersPerPage;
    return teachers.slice(indexOfFirstTeacher, indexOfLastTeacher);
  }, [teachers, teacherCurrentPage, teachersPerPage]);

  const totalTeacherPages = useMemo(() => Math.ceil(teachers.length / teachersPerPage), [teachers.length, teachersPerPage]);

  // Fonctions de navigation optimisées
  const goToPage = useCallback((pageNum) => {
    if (pageNum < 1 || pageNum > totalPages || isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Animation de sortie
    const grid = document.querySelector('.cards-grid');
    if (grid) {
      grid.style.animation = 'slideOutLeft 0.4s ease-out';
    }
    
    setTimeout(() => {
      setCurrentPage(pageNum);
      
      // Animation d'entrée
      requestAnimationFrame(() => {
        const newGrid = document.querySelector('.cards-grid');
        if (newGrid) {
          newGrid.style.animation = 'slideInRight 0.6s ease-out';
        }
        
        // Scroll amélioré pour mobile
        if (window.innerWidth <= 768) {
          // Cherche les tabs de sélection pour remonter plus haut
          const searchTypeMenu = document.querySelector('.search-type-menu');
          
          if (searchTypeMenu) {
            searchTypeMenu.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest' 
            });
            // Décalage pour le header
            setTimeout(() => {
              window.scrollBy(0, -100);  // Augmenté de -20 à -100
            }, 100);
          } else {
            // Fallback sur le titre des résultats
            const resultsTitle = document.querySelector('.results-title');
            if (resultsTitle) {
              resultsTitle.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest' 
              });
              setTimeout(() => {
                window.scrollBy(0, -150);  // Remonte encore plus
              }, 100);
            }
          }
        } else {
          // Pour desktop, comportement normal
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
      
      setTimeout(() => setIsTransitioning(false), 600);
    }, 400);
  }, [totalPages, isTransitioning]);

  const goToStudioPage = useCallback((pageNum) => {
    if (pageNum < 1 || pageNum > totalStudioPages || isTransitioning) return;
    
    setIsTransitioning(true);
    
    const grid = document.querySelector('.cards-grid');
    if (grid) {
      grid.style.animation = 'slideOutLeft 0.3s ease-out';
    }
    
    setTimeout(() => {
      setStudioCurrentPage(pageNum);
      
      requestAnimationFrame(() => {
        const newGrid = document.querySelector('.cards-grid');
        if (newGrid) {
          newGrid.style.animation = 'slideInRight 0.4s ease-out';
        }
        
        // Scroll amélioré pour mobile
        if (window.innerWidth <= 768) {
          const searchTypeMenu = document.querySelector('.search-type-menu');
          
          if (searchTypeMenu) {
            searchTypeMenu.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
            setTimeout(() => {
              window.scrollBy(0, -100);
            }, 100);
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
      
      setTimeout(() => setIsTransitioning(false), 400);
    }, 300);
  }, [totalStudioPages, isTransitioning]);

  const goToTeacherPage = useCallback((pageNum) => {
    if (pageNum < 1 || pageNum > totalTeacherPages || isTransitioning) return;
    
    setIsTransitioning(true);
    
    const grid = document.querySelector('.cards-grid');
    if (grid) {
      grid.style.animation = 'slideOutLeft 0.3s ease-out';
    }
    
    setTimeout(() => {
      setTeacherCurrentPage(pageNum);
      
      requestAnimationFrame(() => {
        const newGrid = document.querySelector('.cards-grid');
        if (newGrid) {
          newGrid.style.animation = 'slideInRight 0.4s ease-out';
        }
        
        // Scroll amélioré pour mobile
        if (window.innerWidth <= 768) {
          const searchTypeMenu = document.querySelector('.search-type-menu');
          
          if (searchTypeMenu) {
            searchTypeMenu.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
            setTimeout(() => {
              window.scrollBy(0, -100);
            }, 100);
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
      
      setTimeout(() => setIsTransitioning(false), 400);
    }, 300);
  }, [totalTeacherPages, isTransitioning]);

  // Déterminer les données pour le SEO selon le type de recherche actuel
  const getCurrentResults = () => {
    switch(searchType) {
      case 'musicians':
        return musicians;
      case 'studios':
        return filteredStudios;
      case 'teachers':
        return teachers;
      default:
        return [];
    }
  };

  const getCurrentFilters = () => {
    switch(searchType) {
      case 'musicians':
        return filters;
      case 'studios':
        return { city: studioCityFilter };
      case 'teachers':
        return teacherFilters;
      default:
        return {};
    }
  };

  const getHasSearched = () => {
    switch(searchType) {
      case 'musicians':
        return hasSearched;
      case 'studios':
        return hasSearchedStudios;
      case 'teachers':
        return hasSearchedTeachers;
      default:
        return false;
    }
  };

  // Metadata pour le SEO
  const pageMetadata = getPageMetadata(searchType, getHasSearched(), getCurrentResults(), getCurrentFilters());

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageMetadata.title}</title>
        <meta name="description" content={pageMetadata.description} />
        <link rel="canonical" href="https://www.gatesofmusic.com/" />
        <link rel="alternate" hreflang="fr-MA" href="https://www.gatesofmusic.com/" />
        <link rel="alternate" hreflang="x-default" href="https://www.gatesofmusic.com/" />

        {/* Open Graph */}
        <meta property="og:title" content={pageMetadata.title} />
        <meta property="og:description" content={pageMetadata.description} />
        <meta property="og:image" content="https://www.gatesofmusic.com/cover.jpg" />
        <meta property="og:url" content="https://www.gatesofmusic.com/" />
        <meta property="og:type" content="website" />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageMetadata.title} />
        <meta name="twitter:description" content={pageMetadata.description} />
        <meta name="twitter:image" content="https://www.gatesofmusic.com/cover.jpg" />

        {/* Données structurées */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Gates of Music",
            "url": "https://www.gatesofmusic.com/",
            "description": pageMetadata.description,
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.gatesofmusic.com/?search={search_term_string}",
              "query-input": "required name=search_term_string"
            },
            "provider": {
              "@type": "Organization",
              "name": "Gates of Music",
              "url": "https://www.gatesofmusic.com/",
              "logo": "https://www.gatesofmusic.com/piano.png"
            }
          })}
        </script>
      </Helmet>

      <div className="homepage-container">
        <Header />
        <Hero />

        {/* Section Sociétés d'Événementiel */}
        <section className="event-companies-section">
          <div className="companies-header">
            <h2 className="companies-title">Sociétés d'Événementiel Recommandées</h2>
            <p className="companies-subtitle">
              Des professionnels expérimentés pour organiser vos événements de A à Z
            </p>
          </div>

          {isLoadingCompanies ? (
            <div className="companies-loading">
              <p>Chargement des sociétés d'événementiel...</p>
            </div>
          ) : eventCompanies.length > 0 ? (
            <div className="companies-grid">
              {eventCompanies.map((company) => (
                <EventCompanyCard key={company.id} company={company} />
              ))}
            </div>
          ) : (
            <div className="companies-empty">
              <p>Aucune société d'événementiel disponible pour le moment.</p>
            </div>
          )}
        </section>

        <h2 className="search-title">Que recherchez-vous ?</h2>

        {/* Menu de sélection */}
        <div className="search-type-menu">
          <button
            className={searchType === 'musicians' ? 'tab active' : 'tab'}
            onClick={() => setSearchType('musicians')}
          >
            Musiciens
          </button>
          <button
            className={searchType === 'studios' ? 'tab active' : 'tab'}
            onClick={() => setSearchType('studios')}
          >
            Studios
          </button>
          <button
            className={searchType === 'teachers' ? 'tab active' : 'tab'}
            onClick={() => setSearchType('teachers')}
          >
            Professeurs
          </button>
        </div>

        {/* Recherches dynamiques selon type */}
        {searchType === 'musicians' && (
          <section className="musician-section">
            <SearchForm onSearch={handleSearch} initialFilters={filters} />

            {musicians.length > 0 ? (
              <>
                <h2 className="results-title">Résultats</h2>
                <div className="cards-grid">
                  {currentMusicians.map((musician) => (
                    <MusicianCard key={musician.id} musician={musician} />
                  ))}
                </div>

                {isAdmin && (
                  <p className="admin-mode">Mode Admin activé</p>
                )}

                <div className="pagination-container">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    Précédent
                  </button>

                  {[...Array(totalPages)].map((_, idx) => {
                    const pageNum = idx + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`pagination-button ${pageNum === currentPage ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Suivant
                  </button>
                </div>
              </>
            ) : hasSearched ? (
              <p className="no-results">Aucun musicien trouvé.</p>
            ) : null}
          </section>
        )}

        {searchType === 'studios' && (
          <section className="studios-section">
            <StudioSearchForm onSearch={handleStudioSearch} />

            {hasSearchedStudios ? (
              filteredStudios.length > 0 ? (
                <>
                  <div className="cards-grid">
                    {currentStudios.map((studio) => (
                      <StudioCard key={studio.id} studio={studio} />
                    ))}
                  </div>

                  <div className="pagination-container">
                    <button onClick={() => goToStudioPage(studioCurrentPage - 1)} disabled={studioCurrentPage === 1} className="pagination-button">← Précédent</button>
                    {[...Array(totalStudioPages)].map((_, idx) => {
                      const pageNum = idx + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => goToStudioPage(pageNum)}
                          className={`pagination-button ${pageNum === studioCurrentPage ? 'active' : ''}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button onClick={() => goToStudioPage(studioCurrentPage + 1)} disabled={studioCurrentPage === totalStudioPages} className="pagination-button">Suivant →</button>
                  </div>
                </>
              ) : (
                <p className="no-results">Aucun studio trouvé.</p>
              )
            ) : (
              <p className="no-results">
                Veuillez effectuer une recherche pour voir les studios.
              </p>
            )}
          </section>
        )}

        {searchType === 'teachers' && (
          <section className="musician-section">
            <TeacherSearchForm onSearch={handleTeacherSearch} />
            {hasSearchedTeachers ? (
              teachers.length > 0 ? (
                <>
                  <div className="cards-grid">
                    {currentTeachers.map((teacher) => (
                      <TeacherCard key={teacher.id} teacher={teacher} />
                    ))}
                  </div>
                  <div className="pagination-container">
                    <button onClick={() => goToTeacherPage(teacherCurrentPage - 1)} disabled={teacherCurrentPage === 1} className="pagination-button">← Précédent</button>
                    {[...Array(totalTeacherPages)].map((_, idx) => (
                      <button key={idx} onClick={() => goToTeacherPage(idx + 1)} className={`pagination-button ${idx + 1 === teacherCurrentPage ? 'active' : ''}`}>{idx + 1}</button>
                    ))}
                    <button onClick={() => goToTeacherPage(teacherCurrentPage + 1)} disabled={teacherCurrentPage === totalTeacherPages} className="pagination-button">Suivant →</button>
                  </div>
                </>
              ) : (
                <p className="no-results">Aucun professeur trouvé.</p>
              )
            ) : (
              <p className="no-results">Veuillez effectuer une recherche pour voir les professeurs.</p>
            )}
          </section>
        )}

        {musicians.length > 0 && (
          <nav className="musicians-directory" aria-label="Annuaire des artistes">
            <h2 className="musicians-directory-title">Nos artistes</h2>
            <ul className="musicians-directory-list">
              {musicians.map(m => (
                <li key={m.id}>
                  <a href={`/musicians/${m.id}`}>
                    {m.name}{m.genres?.length ? ` — ${m.genres.join(', ')}` : ''}{m.city ? ` à ${m.city}` : ''}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <About />
        <Footer />
      </div>
    </>
  );
});

function AppWrapper() {
  const location = useLocation();
  const showContactButton = location.pathname === '/';

  const [musicians, setMusicians] = useState([]);
  const [filters, setFilters] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const docRef = doc(db, 'users', user.email);
        const docSnap = await getDoc(docRef);
        setIsAdmin(docSnap.exists() && docSnap.data().role === 'admin');
      } else {
        setIsAdmin(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      
      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              musicians={musicians}
              setMusicians={setMusicians}
              filters={filters}
              setFilters={setFilters}
              hasSearched={hasSearched}
              setHasSearched={setHasSearched}
              isAdmin={isAdmin}
            />
          }
        />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/musicians/:id" element={<MusicianProfile />} />
        <Route path="/teachers/:id" element={<TeacherProfile />} />   
        <Route path="/studios/:id" element={<StudioProfile />} />
        <Route path="/companies/:id" element={<CompanyProfile />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/account-deletion" element={<AccountDeletion />} />
        <Route
          path="/add-booking-dates"
          element={
            <AdminRoute isAdmin={isAdmin}>
              <AddBookingDates />
            </AdminRoute>
          }
        />
        <Route path="/mon-profil" element={<PrivateRoute><MyProfile /></PrivateRoute>} />
      </Routes>
      {showContactButton && <ContactButton />}
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
}